// utils/sessionRenewal.js
//
// The ONE way the web renews its sign-in. Every renewal point — session restore and the timer in
// AuthContext, the 401 retry in services/secureApi.js and in utils/axiosConfig.js — calls renewSession().
//
// Why one function: the server rotates the renewable token on every renewal, and the token lives in
// localStorage shared by every tab. Four independent renewers (per tab!) used to send the same token at
// the same moment; the loser's 401 then wiped shared storage and signed the whole browser out. Here:
//   - inside a tab, concurrent callers share one in-flight renewal;
//   - across tabs, renewals queue on the browser's lock (navigator.locks), and a tab that waited re-reads
//     storage first — if another tab already renewed, it uses that pass instead of calling the server.
// The server also forgives a duplicate for 30 s (tabs still running older code), but this is the cure.
// Plan: tasks/owner-mobile-app-build.md §10 step 4.

import { storeTokenPair, isTokenExpired } from './tokenValidator';

const LOCK_NAME = 'hostme-session-renewal';
let inFlight = null;

/**
 * True only when the server refused the session (401/400) or there is nothing to renew with. The caller
 * then signs out. Anything else — offline, a 5xx, a timeout — must keep the session: try again later.
 */
export const isSessionRejection = (error) =>
  error?.sessionRejected === true
  || error?.response?.status === 401
  || error?.response?.status === 400;

const withCrossTabLock = (task) =>
  (typeof navigator !== 'undefined' && navigator.locks?.request)
    ? navigator.locks.request(LOCK_NAME, task)
    : task();  // no Web Locks (very old browser): the in-tab single flight still applies

const renewUnderLock = async (failedPass) => {
  const passNow = localStorage.getItem('token');
  if (passNow && passNow !== failedPass && !isTokenExpired(passNow)) {
    return passNow;  // another tab renewed while we waited
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    const error = new Error('No renewable token');
    error.sessionRejected = true;
    throw error;
  }

  // Dynamic import avoids a circular dependency (publicApi → axios config → this file)
  const { default: publicApiService } = await import('../services/api/publicApi');
  const data = await publicApiService.refreshToken(refreshToken);

  // Single writer: refuses if the session ended (logout) while the request was in flight. A grace-window
  // answer carries no new renewable token; storeTokenPair then keeps the one already stored.
  if (!storeTokenPair(data)) {
    throw new Error('Session ended during renewal');
  }
  return data.accessToken;
};

/**
 * Returns a fresh access pass. `failedPass` is the pass the caller already knows is no good (the one a
 * 401 came back on); any other unexpired pass found in storage is used as is.
 */
export const renewSession = (failedPass = localStorage.getItem('token')) => {
  if (!inFlight) {
    inFlight = withCrossTabLock(() => renewUnderLock(failedPass))
      .finally(() => { inFlight = null; });
  }
  return inFlight;
};

export default renewSession;
